import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Search, Eye, Phone, MessageSquare } from "lucide-react";

const CustomersPage = () => {
  const { API, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCustomers();
  }, [search]);

  const fetchCustomers = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      params.append("limit", "50");
      
      const response = await axios.get(`${API}/customers?${params}`, getAuthHeader());
      setCustomers(response.data.customers);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Fetch customers error:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-950" data-testid="customers-title">
            Customers
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {total} total customers registered
          </p>
        </div>
      </div>

      {/* Search */}
      <Card className="border-zinc-200 shadow-none">
        <CardContent className="p-4">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input
              placeholder="Search by name or phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-10 rounded-sm border-zinc-200"
              data-testid="customer-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-zinc-200 shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-zinc-50/50">
                <TableHead className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Customer</TableHead>
                <TableHead className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Phone</TableHead>
                <TableHead className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Bookings</TableHead>
                <TableHead className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Total Spent</TableHead>
                <TableHead className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Last Activity</TableHead>
                <TableHead className="text-xs font-medium text-zinc-500 uppercase tracking-wider text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-zinc-500">
                    Loading customers...
                  </TableCell>
                </TableRow>
              ) : customers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-zinc-500">
                    No customers found
                  </TableCell>
                </TableRow>
              ) : (
                customers.map((customer) => (
                  <TableRow
                    key={customer.id}
                    className="hover:bg-zinc-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/customers/${customer.id}`)}
                    data-testid={`customer-row-${customer.id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-zinc-100 flex items-center justify-center text-sm font-medium text-zinc-600">
                          {customer.name?.charAt(0).toUpperCase() || "?"}
                        </div>
                        <div>
                          <p className="font-medium text-zinc-900">{customer.name}</p>
                          {customer.whatsapp_name && customer.whatsapp_name !== customer.name && (
                            <p className="text-xs text-zinc-500">WhatsApp: {customer.whatsapp_name}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-sm text-zinc-700">
                        <Phone className="w-3 h-3 text-zinc-400" />
                        {customer.phone}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="text-xs">
                        {customer.bookings?.length || 0}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium text-zinc-900">
                      {formatCurrency(customer.total_spent)}
                    </TableCell>
                    <TableCell className="text-sm text-zinc-500">
                      {formatDate(customer.last_activity)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/customers/${customer.id}`);
                        }}
                        data-testid={`view-customer-${customer.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomersPage;
